import { t } from "@web-monorepo/i18n/macro";
import { toast } from "react-toastify";
import { DDSToast } from "@web-monorepo/dds";
import { ApiErrorBody } from "#/src/errorHandling";
import { isApiError } from "@web-monorepo/fetchers";
import { AxiosError } from "axios";
import type { ReactNode } from "react";
import css from "./notification.module.css";

export type NotificationColor = "red" | "green";

const colorToVariant: Record<NotificationColor, "error" | "success"> = {
  red: "error",
  green: "success",
};

export interface NotificationArgs {
  title?: string;
  message?: ReactNode;
  color?: NotificationColor;
  autoClose?: number | false;
  /** A toast already showing with this id is not shown again. */
  toastId?: string;
}

export const showNotification = ({ title, message, color, autoClose, toastId }: NotificationArgs) => {
  const variant = color ? colorToVariant[color] : "neutral";
  const content =
    title && message ? (
      <>
        <strong>{title}</strong> {message}
      </>
    ) : (
      message || title
    );
  toast(<DDSToast variant={variant}>{content}</DDSToast>, {
    autoClose,
    toastId,
  });
};

const isAxiosErrorWithDetail = (error: unknown): error is AxiosError<{ error: ApiErrorBody }> => {
  return typeof error === "object" && error !== null && "isAxiosError" in error && error.isAxiosError === true;
};

// Parses the error body from the error response. Handles both the older
// marketplace useMutation hook (Axios-based) and the newer makeApiMutation
// hook path from @web-monorepo/fetchers (superagent-based).
export const getApiErrorBody = (error: unknown): ApiErrorBody | undefined => {
  if (isAxiosErrorWithDetail(error)) {
    return error.response?.data?.error;
  }
  if (isApiError(error)) {
    return (error.response?.body as { error?: ApiErrorBody } | undefined)?.error;
  }
  return undefined;
};

export const getErrorHandlerWithNotification = ({
  defaultErrorMessage,
  title,
}: {
  defaultErrorMessage?: string;
  title?: string;
}) => {
  return (error: unknown) => {
    showErrorNotification(title, getApiErrorBody(error)?.detail || defaultErrorMessage);
  };
};

export const showErrorNotification = (title?: string, message?: string) => {
  return showNotification({
    title: title || "Something went wrong!",
    message: message || "An error has occurred. Please try again in a few minutes or you may send us a message.",
    color: "red",
  });
};

/**
 * A message catalog failed to load, after its own retries. The page still works, in English, so
 * this says why and stays until dismissed. Shown once however many catalogs fail together.
 */
export const showTranslationErrorNotification = () => {
  showNotification({
    title: t`Translations didn't load.`,
    message: t`Some text is showing in English. Refresh the page to try again.`,
    color: "red",
    autoClose: false,
    toastId: "translation-error",
  });
};

export const showSuccessNotification = (
  message: string,
  options?: {
    actionText?: string;
    actionHref?: string;
    openInNewTab?: boolean;
  },
) => {
  const content =
    options?.actionText && options?.actionHref ? (
      <div className={css.successMessageContainer}>
        <span>{message}</span>
        <a
          href={options.actionHref}
          className={css.successActionLink}
          {...(options.openInNewTab && {
            target: "_blank",
            rel: "noopener noreferrer",
          })}
        >
          {options.actionText}
        </a>
      </div>
    ) : (
      message
    );

  return toast(<DDSToast variant="success">{content}</DDSToast>, {
    autoClose: 5000,
  });
};
